import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Sun, Moon, Monitor, Activity, Plug, Database, User as UserIcon, Info, Pill } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useAthleteProfile } from "@/hooks/use-athlete-profile";
import { usePrefs } from "@/lib/prefs-store";
import { formatError } from "@/lib/format-error";
import { useToast } from "@/components/system";
import { formatShortDate } from "@/lib/date-format";

/**
 * /settings — full surface (BW8 / SPEC 3.0).
 *
 * Sections:
 *   - Profile         (display name, dob, height, weight, goal, units, language)
 *   - Theme & Display (theme dark/light/system, density, tabular numerals info)
 *   - Notifications   (toggles + simple time-of-day pickers)
 *   - Integrations    (link to /integrations)
 *   - Data            (re-load seeds, export — export ships in BW9)
 *   - About           (version, build SHA, spec link)
 *
 * Theme + density preferences sync to localStorage (via usePrefs) AND to the
 * athlete_profile.theme_preference / density_preference columns added in
 * 20260430130000_settings_columns.sql so they roam across devices.
 */

const APP_VERSION = "0.1.0";
const BUILD_SHA = (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? "dev";
const SPEC_LINK = "https://github.com/JonasRackl/Haus/blob/main/SPEC.md";

export function Settings() {
  return (
    <article aria-labelledby="settings-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        Settings
      </p>
      <h1
        id="settings-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-8)",
        }}
      >
        Knobs and dials.
      </h1>

      <div style={{ display: "grid", gap: "var(--space-8)" }}>
        <ProfileSection />
        <GlpSection />
        <ThemeSection />
        <NotificationsSection />
        <IntegrationsSection />
        <DataSection />
        <AboutSection />
      </div>
    </article>
  );
}

function SectionShell({
  Icon,
  title,
  children,
}: {
  Icon: typeof UserIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          marginBottom: "var(--space-3)",
          color: "var(--fg-3)",
          fontSize: "var(--type-meta)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          fontWeight: 500,
        }}
      >
        <Icon size={14} strokeWidth={1.6} aria-hidden /> {title}
      </header>
      <div
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          padding: "var(--space-4)",
          display: "grid",
          gap: "var(--space-3)",
        }}
      >
        {children}
      </div>
    </section>
  );
}

function FieldRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-2) 0",
        borderBottom: "1px solid var(--hair)",
        fontSize: "var(--type-body-s)",
      }}
    >
      <span style={{ color: "var(--fg)" }}>
        {label}
        {hint && (
          <span style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)", marginLeft: 6 }}>
            {hint}
          </span>
        )}
      </span>
      <div>{children}</div>
    </label>
  );
}

type ProfileForm = {
  display_name: string;
  date_of_birth: string;
  height_cm: string;
  current_weight_kg: string;
  goal_weight_kg: string;
  units_preference: string;
  language: string;
};

function ProfileSection() {
  const profile = useAthleteProfile();
  const qc = useQueryClient();
  const toast = useToast();
  const [overrides, setOverrides] = useState<Partial<ProfileForm>>({});

  const form: ProfileForm | null = useMemo(() => {
    if (!profile.data) return null;
    const base: ProfileForm = {
      display_name: profile.data.display_name ?? "",
      date_of_birth: profile.data.date_of_birth ?? "",
      height_cm: profile.data.height_cm?.toString() ?? "",
      current_weight_kg: profile.data.current_weight_kg?.toString() ?? "",
      goal_weight_kg: profile.data.goal_weight_kg?.toString() ?? "",
      units_preference: profile.data.units_preference ?? "metric_with_imperial_secondary",
      language: profile.data.language ?? "en",
    };
    return { ...base, ...overrides };
  }, [profile.data, overrides]);

  function setForm(next: ProfileForm) {
    setOverrides(next);
  }

  const save = useMutation({
    mutationFn: async () => {
      if (!profile.data || !form) throw new Error("No profile loaded");
      const patch = {
        display_name: form.display_name,
        date_of_birth: form.date_of_birth || null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
        current_weight_kg: form.current_weight_kg ? Number(form.current_weight_kg) : null,
        goal_weight_kg: form.goal_weight_kg ? Number(form.goal_weight_kg) : null,
        units_preference: form.units_preference,
        language: form.language,
      };
      const { error } = await supabase
        .from("athlete_profile")
        .update(patch)
        .eq("user_id", profile.data.user_id);
      if (error) throw error;
      // Also write a body_measurement so the weight trend reflects the edit.
      if (
        form.current_weight_kg &&
        Number(form.current_weight_kg) !== profile.data.current_weight_kg
      ) {
        await supabase.from("body_measurement").insert({
          user_id: profile.data.user_id,
          ts: new Date().toISOString(),
          weight_kg: Number(form.current_weight_kg),
          sensor_source: "manual",
          time_of_day: "other",
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete_profile"] });
      qc.invalidateQueries({ queryKey: ["weight_trend"] });
      toast.show("Profile saved.");
    },
    onError: (err) => toast.show(`Save failed — ${formatError(err)}`),
  });

  if (profile.isLoading || form == null) {
    return (
      <SectionShell Icon={UserIcon} title="Profile">
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>Loading…</p>
      </SectionShell>
    );
  }
  if (!profile.data) {
    return (
      <SectionShell Icon={UserIcon} title="Profile">
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          No profile yet. <Link to="/onboarding" style={{ color: "var(--accent)" }}>Run onboarding</Link>.
        </p>
      </SectionShell>
    );
  }

  return (
    <SectionShell Icon={UserIcon} title="Profile">
      <FieldRow label="Display name">
        <Input
          value={form.display_name}
          onChange={(v) => setForm({ ...form, display_name: v })}
        />
      </FieldRow>
      <FieldRow
        label="Sex at birth"
        hint="Set at onboarding"
      >
        <span className="mono" style={{ color: "var(--fg-3)" }}>
          {profile.data.sex_at_birth ?? "—"}
        </span>
      </FieldRow>
      <FieldRow label="Date of birth">
        <Input
          type="date"
          value={form.date_of_birth}
          onChange={(v) => setForm({ ...form, date_of_birth: v })}
        />
      </FieldRow>
      <FieldRow label="Height" hint="cm">
        <Input
          type="number"
          value={form.height_cm}
          onChange={(v) => setForm({ ...form, height_cm: v })}
          width={84}
        />
      </FieldRow>
      <FieldRow label="Current weight" hint="kg">
        <Input
          type="number"
          value={form.current_weight_kg}
          onChange={(v) => setForm({ ...form, current_weight_kg: v })}
          width={84}
        />
      </FieldRow>
      <FieldRow label="Goal weight" hint="kg">
        <Input
          type="number"
          value={form.goal_weight_kg}
          onChange={(v) => setForm({ ...form, goal_weight_kg: v })}
          width={84}
        />
      </FieldRow>
      <FieldRow label="Units">
        <Select
          value={form.units_preference}
          onChange={(v) => setForm({ ...form, units_preference: v })}
          options={[
            { value: "metric", label: "Metric" },
            { value: "imperial", label: "Imperial" },
            { value: "metric_with_imperial_secondary", label: "Metric · imperial side-by-side" },
          ]}
        />
      </FieldRow>
      <FieldRow label="Language">
        <Select
          value={form.language}
          onChange={(v) => setForm({ ...form, language: v })}
          options={[
            { value: "en", label: "English" },
            { value: "de", label: "Deutsch" },
          ]}
        />
      </FieldRow>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "var(--space-2)" }}>
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          style={{
            padding: "8px 16px",
            background: "var(--accent)",
            color: "#0a0b0d",
            border: "none",
            borderRadius: "var(--radius-card)",
            fontSize: "var(--type-body-s)",
            fontWeight: 500,
            cursor: save.isPending ? "not-allowed" : "pointer",
          }}
        >
          {save.isPending ? "Saving…" : "Save profile"}
        </button>
      </div>
    </SectionShell>
  );
}

/**
 * GLP-1 / GIP receptor agonist awareness (BW18 PR-I).
 *
 * Toggle in /settings/profile. When on, expose drug + dose + last titration
 * + opt-in eat-on-schedule reminders. Drives suggestion variants and the
 * /today protein-per-meal widget.
 */
const GLP1_DRUGS = [
  { value: "", label: "—" },
  { value: "tirzepatide", label: "Tirzepatide (Mounjaro / Zepbound)" },
  { value: "semaglutide", label: "Semaglutide (Wegovy / Ozempic)" },
  { value: "liraglutide", label: "Liraglutide (Saxenda / Victoza)" },
  { value: "dulaglutide", label: "Dulaglutide (Trulicity)" },
  { value: "other", label: "Other" },
];

const GLP1_SIDE_EFFECT_OPTIONS = [
  "nausea",
  "fatigue",
  "constipation",
  "diarrhea",
  "appetite_loss",
  "reflux",
];

function GlpSection() {
  const profile = useAthleteProfile();
  const qc = useQueryClient();
  const toast = useToast();

  type Row = {
    on_glp1: boolean;
    glp1_drug: string;
    glp1_dose_mg: string;
    glp1_last_titration: string;
    glp1_side_effects: string[];
    eat_on_schedule_push: boolean;
  };

  const initial: Row | null = useMemo(() => {
    if (!profile.data) return null;
    const prefs =
      (profile.data.notification_preferences as { glp1_eat_on_schedule_push?: boolean } | null) ?? {};
    const sides = Array.isArray(profile.data.glp1_side_effects)
      ? (profile.data.glp1_side_effects as string[])
      : [];
    return {
      on_glp1: Boolean(profile.data.on_glp1),
      glp1_drug: profile.data.glp1_drug ?? "",
      glp1_dose_mg: profile.data.glp1_dose_mg?.toString() ?? "",
      glp1_last_titration: profile.data.glp1_last_titration ?? "",
      glp1_side_effects: sides,
      eat_on_schedule_push: Boolean(prefs.glp1_eat_on_schedule_push),
    };
  }, [profile.data]);

  const [overrides, setOverrides] = useState<Partial<Row>>({});
  const form: Row | null = initial ? { ...initial, ...overrides } : null;

  const save = useMutation({
    mutationFn: async (next: Row) => {
      if (!profile.data) throw new Error("No profile loaded");
      const existingPrefs =
        (profile.data.notification_preferences as Record<string, unknown> | null) ?? {};
      const patch = {
        on_glp1: next.on_glp1,
        glp1_drug: next.on_glp1 ? next.glp1_drug || null : null,
        glp1_dose_mg:
          next.on_glp1 && next.glp1_dose_mg ? Number(next.glp1_dose_mg) : null,
        glp1_last_titration:
          next.on_glp1 && next.glp1_last_titration ? next.glp1_last_titration : null,
        glp1_side_effects: next.on_glp1 ? next.glp1_side_effects : [],
        notification_preferences: {
          ...existingPrefs,
          glp1_eat_on_schedule_push: next.eat_on_schedule_push,
        },
      };
      const { error } = await supabase
        .from("athlete_profile")
        .update(patch)
        .eq("user_id", profile.data.user_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete_profile"] });
      toast.show("GLP-1 settings saved.");
      setOverrides({});
    },
    onError: (err) => toast.show(`Save failed, ${formatError(err)}`),
  });

  function set<K extends keyof Row>(key: K, value: Row[K]) {
    if (!form) return;
    const next = { ...form, [key]: value };
    setOverrides(next);
    save.mutate(next);
  }

  if (profile.isLoading || !form) {
    return (
      <SectionShell Icon={Pill} title="GLP-1 awareness">
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>Loading…</p>
      </SectionShell>
    );
  }

  return (
    <SectionShell Icon={Pill} title="GLP-1 awareness">
      <FieldRow
        label="I am taking a GLP-1 / GIP receptor agonist"
        hint="Tirzepatide, semaglutide, etc."
      >
        <Toggle
          checked={form.on_glp1}
          onChange={(v) => set("on_glp1", v)}
        />
      </FieldRow>
      {form.on_glp1 && (
        <>
          <FieldRow label="Drug">
            <Select
              value={form.glp1_drug}
              onChange={(v) => set("glp1_drug", v)}
              options={GLP1_DRUGS}
            />
          </FieldRow>
          <FieldRow label="Current dose" hint="mg">
            <Input
              type="number"
              value={form.glp1_dose_mg}
              onChange={(v) => set("glp1_dose_mg", v)}
              width={84}
            />
          </FieldRow>
          <FieldRow label="Last titration">
            <Input
              type="date"
              value={form.glp1_last_titration}
              onChange={(v) => set("glp1_last_titration", v)}
            />
          </FieldRow>
          <FieldRow label="Side effects experienced" hint="select all that apply">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
              {GLP1_SIDE_EFFECT_OPTIONS.map((opt) => {
                const active = form.glp1_side_effects.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      const next = active
                        ? form.glp1_side_effects.filter((x) => x !== opt)
                        : [...form.glp1_side_effects, opt];
                      set("glp1_side_effects", next);
                    }}
                    style={{
                      padding: "4px 10px",
                      background: active ? "var(--chip-hi)" : "transparent",
                      color: active ? "var(--fg)" : "var(--fg-3)",
                      border: `1px solid ${active ? "var(--accent-dim)" : "var(--hair-2)"}`,
                      borderRadius: "var(--radius-chip)",
                      fontSize: "var(--type-meta)",
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {opt.replace("_", " ")}
                  </button>
                );
              })}
            </div>
          </FieldRow>
          <FieldRow
            label="Eat-on-schedule reminders"
            hint="push every 4h during daytime"
          >
            <Toggle
              checked={form.eat_on_schedule_push}
              onChange={(v) => set("eat_on_schedule_push", v)}
            />
          </FieldRow>
          <p
            style={{
              margin: "var(--space-2) 0 0",
              padding: "var(--space-2) var(--space-3)",
              background: "var(--accent-wash)",
              borderRadius: "var(--radius-card)",
              fontSize: "var(--type-meta)",
              color: "var(--fg-2)",
              lineHeight: 1.5,
            }}
          >
            Profile flag drives variant suggestions, the protein-per-meal widget on Today,
            and a hunger trend chart at <Link to="/health/glp1" style={{ color: "var(--accent)" }}>/health/glp1</Link>.
          </p>
        </>
      )}
    </SectionShell>
  );
}

function ThemeSection() {
  const { theme, density, weekStart, setTheme, setDensity, setWeekStart } =
    usePrefs();
  const profile = useAthleteProfile();
  const qc = useQueryClient();

  const persistTheme = useMutation({
    mutationFn: async (val: string) => {
      if (!profile.data) return;
      const { error } = await supabase
        .from("athlete_profile")
        .update({ theme_preference: val })
        .eq("user_id", profile.data.user_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athlete_profile"] }),
  });
  const persistDensity = useMutation({
    mutationFn: async (val: string) => {
      if (!profile.data) return;
      const { error } = await supabase
        .from("athlete_profile")
        .update({ density_preference: val })
        .eq("user_id", profile.data.user_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["athlete_profile"] }),
  });

  return (
    <SectionShell Icon={Monitor} title="Theme & Display">
      <FieldRow label="Theme">
        <SegmentedToggle
          value={theme === "dark" ? "dark" : "light"}
          options={[
            { value: "dark", label: <Moon size={14} strokeWidth={1.6} /> },
            { value: "light", label: <Sun size={14} strokeWidth={1.6} /> },
          ]}
          onChange={(v) => {
            setTheme(v as "dark" | "light");
            persistTheme.mutate(v);
          }}
        />
      </FieldRow>
      <FieldRow label="Density">
        <SegmentedToggle
          value={density}
          options={[
            { value: "comfortable", label: "Comfort" },
            { value: "compact", label: "Compact" },
          ]}
          onChange={(v) => {
            setDensity(v as "comfortable" | "compact");
            persistDensity.mutate(v);
          }}
        />
      </FieldRow>
      <FieldRow
        label="Week starts on"
        hint="Used by calendar, meal plan, shopping"
      >
        <SegmentedToggle
          value={String(weekStart)}
          options={[
            { value: "1", label: "Mon" },
            { value: "0", label: "Sun" },
            { value: "6", label: "Sat" },
          ]}
          onChange={(v) => {
            const next = (v === "0" ? 0 : v === "6" ? 6 : 1) as 0 | 1 | 6;
            setWeekStart(next);
          }}
        />
      </FieldRow>
      <FieldRow label="Tabular numerals" hint="always on">
        <span className="mono" style={{ color: "var(--fg-3)" }}>1234567890</span>
      </FieldRow>
    </SectionShell>
  );
}

function NotificationsSection() {
  const profile = useAthleteProfile();
  const qc = useQueryClient();
  const toast = useToast();

  const prefs = useMemo(() => {
    const raw = (profile.data as { notification_preferences?: Record<string, unknown> } | null)
      ?.notification_preferences ?? {};
    return {
      daily_brief_email: Boolean((raw as { daily_brief_email?: boolean }).daily_brief_email),
      daily_brief_time: String((raw as { daily_brief_time?: string }).daily_brief_time ?? "07:00"),
      critical_rules_push: Boolean((raw as { critical_rules_push?: boolean }).critical_rules_push),
      weekly_summary_email: Boolean((raw as { weekly_summary_email?: boolean }).weekly_summary_email),
      weekly_summary_day: String((raw as { weekly_summary_day?: string }).weekly_summary_day ?? "sunday"),
    };
  }, [profile.data]);

  const save = useMutation({
    mutationFn: async (next: typeof prefs) => {
      if (!profile.data) throw new Error("No profile loaded");
      const { error } = await supabase
        .from("athlete_profile")
        .update({ notification_preferences: next })
        .eq("user_id", profile.data.user_id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["athlete_profile"] });
      toast.show("Notification preferences saved.");
    },
    onError: (err) => toast.show(`Save failed — ${formatError(err)}`),
  });

  function set(field: keyof typeof prefs, value: string | boolean) {
    save.mutate({ ...prefs, [field]: value });
  }

  return (
    <SectionShell Icon={Activity} title="Notifications">
      <FieldRow label="Daily brief email">
        <Toggle
          checked={prefs.daily_brief_email}
          onChange={(v) => set("daily_brief_email", v)}
        />
      </FieldRow>
      <FieldRow label="Daily brief time">
        <Input
          type="time"
          value={prefs.daily_brief_time}
          onChange={(v) => set("daily_brief_time", v)}
          width={104}
        />
      </FieldRow>
      <FieldRow label="Critical rule pushes" hint="iOS PWA only">
        <Toggle
          checked={prefs.critical_rules_push}
          onChange={(v) => set("critical_rules_push", v)}
        />
      </FieldRow>
      <FieldRow label="Weekly summary email">
        <Toggle
          checked={prefs.weekly_summary_email}
          onChange={(v) => set("weekly_summary_email", v)}
        />
      </FieldRow>
      <FieldRow label="Weekly summary day">
        <Select
          value={prefs.weekly_summary_day}
          onChange={(v) => set("weekly_summary_day", v)}
          options={[
            { value: "monday", label: "Monday" },
            { value: "sunday", label: "Sunday" },
            { value: "saturday", label: "Saturday" },
          ]}
        />
      </FieldRow>
    </SectionShell>
  );
}

function IntegrationsSection() {
  return (
    <SectionShell Icon={Plug} title="Integrations">
      <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
        Manage Strava, Health Auto Export, and future Garmin / Apple Health connections.
      </p>
      <Link
        to="/integrations"
        style={{
          marginTop: "var(--space-2)",
          alignSelf: "flex-start",
          display: "inline-flex",
          padding: "8px 14px",
          background: "transparent",
          border: "1px solid var(--hair-2)",
          borderRadius: "var(--radius-card)",
          color: "var(--fg)",
          fontSize: "var(--type-body-s)",
          textDecoration: "none",
        }}
      >
        Open integrations →
      </Link>
    </SectionShell>
  );
}

function DataSection() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const toast = useToast();

  const usage = useAiUsageThisMonth(userId);

  const reloadSeeds = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const results: Record<string, unknown> = {};
      for (const fn of [
        "bootstrap_user_seeds",
        "bootstrap_projects_seeds",
        "bootstrap_supplement_seeds",
      ]) {
        const { data, error } = await supabase.rpc(fn as "bootstrap_user_seeds");
        if (error) {
          // Skip if function name doesn't exist on this DB; otherwise rethrow.
          if (error.code === "PGRST202") continue;
          throw error;
        }
        results[fn] = data;
      }
      return results;
    },
    onSuccess: () => toast.show("Seeds reloaded."),
    onError: (err) => toast.show(`Seed reload failed — ${formatError(err)}`),
  });

  return (
    <SectionShell Icon={Database} title="Data">
      <FieldRow label="AI usage this month" hint="Photo extraction · Anthropic">
        <span className="mono" style={{ color: "var(--fg)" }}>
          {usage.isLoading ? "…" : `$${usage.total.toFixed(2)}`}
          {usage.calls > 0 && (
            <span style={{ color: "var(--fg-3)", marginLeft: 6 }}>
              · {usage.calls} call{usage.calls === 1 ? "" : "s"}
            </span>
          )}
        </span>
      </FieldRow>
      <FieldRow label="Re-load seeds" hint="Sport · Evidence · Project · Supplement">
        <button
          type="button"
          onClick={() => reloadSeeds.mutate()}
          disabled={reloadSeeds.isPending}
          style={{
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-chip)",
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
            cursor: reloadSeeds.isPending ? "not-allowed" : "pointer",
          }}
        >
          {reloadSeeds.isPending ? "Loading…" : "Re-run"}
        </button>
      </FieldRow>
      <FieldRow label="Export all data" hint="JSON download — BW9">
        <button
          type="button"
          disabled
          style={{
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-chip)",
            color: "var(--fg-3)",
            fontSize: "var(--type-body-s)",
            cursor: "not-allowed",
          }}
        >
          Coming soon
        </button>
      </FieldRow>
      <FieldRow label="Delete account" hint="Irreversible">
        <button
          type="button"
          onClick={() => {
            const confirmed = window.confirm(
              "This permanently deletes every Haus row tied to your account. Continue?",
            );
            if (!confirmed) return;
            const second = window.prompt(`Type "delete" to confirm.`);
            if (second !== "delete") return;
            toast.show(
              "Account deletion is manual for v1 — email contact@jonasrackl.com to request a wipe.",
              { durationMs: 8000 },
            );
          }}
          style={{
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid var(--brick)",
            borderRadius: "var(--radius-chip)",
            color: "var(--brick)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
          }}
        >
          Delete…
        </button>
      </FieldRow>
    </SectionShell>
  );
}

function AboutSection() {
  return (
    <SectionShell Icon={Info} title="About">
      <FieldRow label="Version">
        <span className="mono">{APP_VERSION}</span>
      </FieldRow>
      <FieldRow label="Build">
        <span className="mono" style={{ color: "var(--fg-3)" }}>
          {BUILD_SHA}
        </span>
      </FieldRow>
      <FieldRow label="Build date">
        <span className="mono" style={{ color: "var(--fg-3)" }}>
          {formatShortDate(new Date())}
        </span>
      </FieldRow>
      <FieldRow label="Spec">
        <a
          href={SPEC_LINK}
          target="_blank"
          rel="noreferrer noopener"
          style={{ color: "var(--accent)" }}
        >
          GitHub →
        </a>
      </FieldRow>
    </SectionShell>
  );
}

function Input({
  value,
  onChange,
  type = "text",
  width,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  width?: number;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: "var(--bg-3)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-chip)",
        color: "var(--fg)",
        fontSize: "var(--type-body-s)",
        padding: "6px 10px",
        width: width ?? 160,
      }}
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: "var(--bg-3)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-chip)",
        color: "var(--fg)",
        fontSize: "var(--type-body-s)",
        padding: "6px 10px",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 40,
        height: 22,
        borderRadius: 999,
        background: checked ? "var(--accent)" : "var(--bg-4)",
        border: "1px solid var(--hair-2)",
        position: "relative",
        cursor: "pointer",
        padding: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 1,
          left: checked ? 19 : 1,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#0a0b0d",
          transition: "left var(--dur-fast) var(--ease-out)",
        }}
      />
    </button>
  );
}

function SegmentedToggle({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: React.ReactNode }[];
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        background: "var(--bg-3)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-chip)",
        padding: 2,
        gap: 2,
      }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          style={{
            padding: "4px 10px",
            background: value === o.value ? "var(--chip-hi)" : "transparent",
            color: value === o.value ? "var(--fg)" : "var(--fg-3)",
            border: "none",
            borderRadius: "var(--radius-chip)",
            fontSize: "var(--type-body-s)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function useAiUsageThisMonth(userId: string | undefined) {
  const q = useQuery({
    queryKey: ["ai_usage", userId, monthKey()],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return { total: 0, calls: 0 };
      const monthStart = monthStartIso();
      const { data, error } = await supabase
        .from("ai_usage_log")
        .select("cost_usd")
        .eq("user_id", userId)
        .gte("created_at", monthStart);
      if (error) throw error;
      const total = (data ?? []).reduce(
        (acc, r) => acc + Number(r.cost_usd ?? 0),
        0,
      );
      return { total, calls: data?.length ?? 0 };
    },
  });
  return {
    isLoading: q.isLoading,
    total: q.data?.total ?? 0,
    calls: q.data?.calls ?? 0,
  };
}

function monthStartIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}

function monthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${now.getUTCMonth() + 1}`;
}
